import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Button,
  Divider,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import { useGlobalState } from '../../global/context/global-context';
import { getFormattedEntityName } from '../../../utils/entity-formatter';
import { useParams } from 'react-router-dom';
import { useNavigate } from '../../../hooks/useNavigate';

export default function AppFooter() {
  const { applicationData, parametersPortal } = useGlobalState();
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();
  const cnpj = applicationData?.entity?.document || '';
  const operatingHours = parametersPortal?.operatingHours || 'Segunda a Sexta: 8h às 17h';

  const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

  const getHref = (path: string) => {
    if (params.state && params.city) {
      return `/${params.state}/${params.city}${path}`;
    }
    return path;
  };

  const quickLinks = [
    { label: 'Início', href: '/', external: false },
    { label: 'A Cidade', href: '/a-cidade', external: false },
    { label: 'Notícias', href: '/noticias', external: false },
    { label: 'Diário Oficial', href: '/diario-oficial', external: false },
    { label: 'Licitações', href: '/licitacoes', external: false },
    { label: 'Contas Públicas', href: '/contas-publicas', external: false },
    { label: 'Contato', href: '/contato', external: false },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'white',
        pt: 6,
        pb: 3,
        mt: 8,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Logo e Informações */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={
                applicationData?.entity?.brandLogo
                  ? `data:image/png;base64,${applicationData?.entity?.brandLogo}`
                  : applicationData?.entity?.stateBrandLogo
              }
              alt="Logo"
              sx={{
                height: 60,
                width: 'auto',
                mb: 2,
              }}
            />
            <Typography variant="h6" gutterBottom fontWeight={700}>
              {getFormattedEntityName(applicationData?.entity)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              CNPJ: {applicationData?.entity?.formmatedDocument}
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                component="a"
                href={`http://docgedsistemas.com.br/ouvidoria?codEntidade=${cnpj}&empresa=3851`}
                target="_blank"
                rel="noopener"
                sx={{ borderRadius: 2 }}
              >
                e-OUV
              </Button>
              <Button
                variant="outlined"
                size="small"
                component="a"
                href={`http://docgedsistemas.com.br/sic?codEntidade=${cnpj}&empresa=3851`}
                target="_blank"
                rel="noopener"
                sx={{ borderRadius: 2 }}
              >
                e-SIC
              </Button>
            </Box>
          </Grid>

          {/* Navegação */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" gutterBottom fontWeight={700}>
              Navegar pelo site
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  href={getHref(link.href)}
                  color="text.secondary"
                  underline="hover"
                  onClick={(e) => {
                    if (!link.external) {
                      e.preventDefault();
                      navigate(link.href);
                    }
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Contato */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom fontWeight={700}>
              Gabinete do Prefeito
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Órgão de assessoramento direto, que auxilia o processo decisório do Chefe do Poder
              Executivo.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {applicationData?.entity?.contact?.formattedBusinessPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {applicationData?.entity?.contact?.formattedBusinessPhone}
                    {applicationData?.entity?.contact?.formattedMessagePhone &&
                      ` / ${applicationData?.entity?.contact?.formattedMessagePhone}`}
                  </Typography>
                </Box>
              )}

              {applicationData?.entity?.contact?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {applicationData?.entity?.contact?.email}
                  </Typography>
                </Box>
              )}

              {applicationData?.entity?.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOnIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    {applicationData?.entity?.address?.street}, {applicationData?.entity?.address?.number},{' '}
                    {applicationData?.entity?.address?.neighborhood},{' '}
                    {applicationData?.entity?.address?.city} - {applicationData?.entity?.address?.state},{' '}
                    {applicationData?.entity?.address?.formattedZipCode}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {operatingHours}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} - {getFormattedEntityName(applicationData?.entity)} - Todos os direitos
            reservados
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Desenvolvido por DocGed Serviços
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Link
              href={`https://validator.w3.org/nu/?doc=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <Box
                component="img"
                src="https://www.w3.org/Icons/valid-html401"
                alt="Valid HTML"
                sx={{
                  height: 31,
                  width: 88,
                }}
              />
            </Link>

            {isSecure && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'success.light',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'success.main',
                }}
              >
                <LockIcon fontSize="small" sx={{ color: 'success.dark' }} />
                <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600 }}>
                  Site Seguro
                </Typography>
              </Box>
            )}

            <Link
              href="https://radardatransparencia.atricon.org.br/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/images/logo_radar_transparencia.jpg`}
                alt="Radar da Transparência"
                sx={{
                  height: 40,
                  width: 'auto',
                }}
              />
            </Link>

            <Link
              href={getHref('/acessibilidade')}
              onClick={(e) => {
                e.preventDefault();
                navigate('/acessibilidade');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/assets/images/logo_acessibilidade.png`}
                alt="Acessibilidade"
                sx={{
                  height: 40,
                  width: 'auto',
                }}
              />
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
